import { useEffect, useState } from 'react';

import { getMySubmissionsAction } from '@/server/actions/prediction/getMySubmissions.action';
import { T_MySubmission } from '@/ts/types/T_MySubmissions';

interface I_UseMySubmissionsProps {
  predictionAddress?: string | null;
  predictionId?: string | null;
}

export const useMySubmissions = ({
  predictionAddress,
  predictionId
}: I_UseMySubmissionsProps) => {
  const [submissions, setSubmissions] = useState<T_MySubmission[]>([]);
  const [total, setTotal] = useState(0);
  const [winningPrices, setWinningPrices] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchSubmissions = async () => {
      if (!predictionAddress || !predictionId) {
        setSubmissions([]);
        setTotal(0);
        return;
      }

      setIsLoading(true);
      try {
        const response = await getMySubmissionsAction(
          predictionAddress,
          predictionId,
          20,
          0
        );
        if (response?.data) {
          setSubmissions(response.data.submissions);
          setTotal(response.data.total);
          setWinningPrices(response.data.winningPrices || []);
        }
      } catch (error) {
        console.error('Failed to fetch submissions:', error);
        setSubmissions([]);
        setTotal(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubmissions();
  }, [predictionAddress, predictionId]);

  const handleLoadMore = async () => {
    if (!predictionAddress || !predictionId || isLoading) return;
    if (submissions.length >= total) return;

    setIsLoading(true);
    try {
      const currentOffset = submissions.length;
      const response = await getMySubmissionsAction(
        predictionAddress,
        predictionId,
        20,
        currentOffset
      );
      if (response?.data) {
        setSubmissions(prev => [...prev, ...response.data.submissions]);
        setTotal(response.data.total);
        // Update winningPrices if provided (should be same for all pages)
        if (
          response.data.winningPrices &&
          response.data.winningPrices.length > 0
        ) {
          setWinningPrices(response.data.winningPrices);
        }
      }
    } catch (error) {
      console.error('Failed to load more submissions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    submissions,
    total,
    winningPrices,
    isLoading,
    handleLoadMore
  };
};
